import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ChevronRight, Shield, Leaf } from 'lucide-react';
import { FAQAccordion } from '../../components/FAQAccordion';
import { CTAStrip } from '../../components/CTAStrip';
import { InlineCostCalculator } from '../../components/InlineCostCalculator';
const included = ['Full or partial property clearance', 'All waste types accepted', 'Furniture, appliances & electricals', 'Clothing & personal effects', 'Garden waste included', 'Loft & basement clearance', 'Responsible recycling & donation', 'Waste transfer note provided'];
const idealFor = [{
  title: 'Bereavement Clearances',
  desc: "Sensitive, respectful clearance of a loved one's home."
}, {
  title: 'End of Tenancy',
  desc: 'Fast clearance to meet landlord or letting agent deadlines.'
}, {
  title: 'Downsizing',
  desc: 'Clear unwanted items when moving to a smaller property.'
}, {
  title: 'Renovation Prep',
  desc: 'Clear a property before building work begins.'
}];
const steps = [{
  num: '1',
  title: 'Contact Us',
  desc: 'Call, WhatsApp, or fill in our quote form with details of the property.'
}, {
  num: '2',
  title: 'Free Survey',
  desc: "We'll assess the job (in person or via photos) and provide a fixed price."
}, {
  num: '3',
  title: 'Clearance Day',
  desc: 'Our team arrives on time and works efficiently to clear the property.'
}, {
  num: '4',
  title: 'Responsible Disposal',
  desc: 'All waste is sorted, recycled, donated, or disposed of legally.'
}];
const faqs = [{
  question: 'How much does a house clearance cost?',
  answer: 'Prices start from £150 for small clearances and vary based on volume, access, and location. We provide a fixed price after assessing the job — no hidden fees.'
}, {
  question: 'How quickly can you clear a property?',
  answer: "Most house clearances can be completed in one day. Same-day service is available for urgent cases. We'll give you a realistic timeframe when we quote."
}, {
  question: 'Do you recycle everything you collect?',
  answer: 'We aim to recycle or donate 90%+ of everything we collect. Usable furniture and clothing go to local charities. Only items that cannot be reused go to licensed disposal facilities.'
}, {
  question: 'Are you insured for house clearances?',
  answer: 'Yes. We carry full public liability insurance and all operatives are fully trained. Your property is protected throughout the clearance.'
}, {
  question: 'Do I need to be present during the clearance?',
  answer: "Not necessarily. Many clients provide key access and we handle everything. We'll keep you updated throughout and send photos on completion."
}];
export function HouseClearancePage() {
  return <div className="pt-16 lg:pt-20" data-id="element-900">
      {/* Hero */}
      <section className="relative h-72 md:h-96 overflow-hidden" data-id="element-901">
        <img src="https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=1600&auto=format&fit=crop" alt="House clearance service" className="w-full h-full object-cover" data-id="element-902" />
        <div className="absolute inset-0 hero-overlay" data-id="element-903" />
        <div className="absolute inset-0 flex items-center" data-id="element-904">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-905">
            <nav className="flex items-center gap-2 text-green-200 text-sm mb-4" data-id="element-906">
              <Link to="/" className="hover:text-white" data-id="element-907">
                Home
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-908" />
              <Link to="/services" className="hover:text-white" data-id="element-909">
                Services
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-910" />
              <span className="text-white" data-id="element-911">House Clearance</span>
            </nav>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-912">
              House Clearance Services in North & East London
            </h1>
            <p className="text-green-100 mt-3 text-lg" data-id="element-913">
              Licensed, insured, eco-friendly. Serving Chingford, Enfield & all
              London. From £150.
            </p>
          </div>
        </div>
      </section>

      {/* What's Included — with image */}
      <section className="py-16 px-4 bg-white" data-id="element-914">
        <div className="max-w-7xl mx-auto" data-id="element-915">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-916">
            <div data-id="element-917">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-918">
                What's Included
              </h2>
              <ul className="space-y-3" data-id="element-919">
                {included.map(item => <li key={item} className="flex items-center gap-3" data-id="element-920">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" style={{
                  color: '#1B8F4B'
                }} data-id="element-921" />
                    <span className="text-gray-700" data-id="element-922">{item}</span>
                  </li>)}
              </ul>

              {/* Relevant house clearance image */}
              <div className="mt-8 rounded-2xl overflow-hidden shadow-lg" data-id="element-923">
                <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&auto=format&fit=crop" alt="Professional house clearance team at work" className="w-full h-56 object-cover" data-id="element-924" />
              </div>
            </div>
            <div data-id="element-925">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-926">
                Ideal For
              </h2>
              <div className="space-y-4" data-id="element-927">
                {idealFor.map(({
                title,
                desc
              }) => <div key={title} className="p-4 rounded-xl border border-gray-100 hover:border-[#1B8F4B] transition-colors" data-id="element-928">
                    <h3 className="font-bold text-gray-900 mb-1 text-sm" style={{
                  fontFamily: 'Montserrat, sans-serif'
                }} data-id="element-929">
                      {title}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-930">{desc}</p>
                  </div>)}
              </div>

              {/* Quick navigation to other services */}
              <div className="mt-8 p-5 rounded-2xl border border-gray-100 bg-gray-50" data-id="element-931">
                <h3 className="font-bold text-gray-900 mb-3 text-sm" style={{
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-932">
                  Browse Other Services
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-933">
                  {[['Office Removals', '/services/office-removals'], ['Garden Waste', '/services/garden-waste'], ['Commercial Waste', '/services/commercial-waste'], ['Overseas Removals', '/services/overseas-removals']].map(([label, href]) => <Link key={href} to={href} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:border-[#1B8F4B] hover:text-[#1B8F4B] transition-colors text-gray-600" data-id="element-934">
                      {label}
                    </Link>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 px-4 texture-paper" data-id="element-935">
        <div className="max-w-7xl mx-auto" data-id="element-936">
          <h2 className="text-2xl font-bold text-gray-900 mb-10 text-center" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-937">
            Our Process
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-938">
            {steps.map(({
            num,
            title,
            desc
          }) => <div key={num} className="text-center" data-id="element-939">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg" style={{
              backgroundColor: '#1B8F4B',
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-940">
                  {num}
                </div>
                <h3 className="font-bold text-gray-900 mb-2" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-941">
                  {title}
                </h3>
                <p className="text-sm text-gray-500" data-id="element-942">{desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-4 bg-white" data-id="element-943">
        <div className="max-w-3xl mx-auto text-center" data-id="element-944">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-945">
            Pricing
          </h2>
          <div className="rounded-2xl border-2 border-[#1B8F4B] p-8 mb-6" data-id="element-946">
            <p className="text-4xl font-extrabold mb-2" style={{
            color: '#1B8F4B',
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-947">
              From £150
            </p>
            <p className="text-gray-500 mb-6" data-id="element-948">
              Guide price for a small house clearance. Final price confirmed
              after photos or site visit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-949">
              <Link to="/contact" className="px-8 py-4 rounded-xl font-bold text-white transition-colors hover:opacity-90" style={{
              backgroundColor: '#1B8F4B',
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-950">
                Request a Quote
              </Link>
              <Link to="/calculator" className="px-8 py-4 rounded-xl font-bold border-2 transition-colors hover:bg-gray-50" style={{
              borderColor: '#1B8F4B',
              color: '#1B8F4B',
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-951">
                Full Cost Calculator
              </Link>
            </div>
          </div>
          <div className="flex justify-center gap-6" data-id="element-952">
            <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-953">
              <Shield className="w-4 h-4" style={{
              color: '#1B8F4B'
            }} data-id="element-954" />{' '}
              Licensed & Fully Insured
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-955">
              <Leaf className="w-4 h-4" style={{
              color: '#1B8F4B'
            }} data-id="element-956" />{' '}
              Eco-Friendly Disposal
            </div>
          </div>
        </div>
      </section>

      {/* Inline Cost Calculator */}
      <InlineCostCalculator defaultService="house-clearance" serviceName="House Clearance" data-id="element-957" />

      {/* FAQ */}
      <section className="py-16 px-4 bg-white" data-id="element-958">
        <div className="max-w-3xl mx-auto" data-id="element-959">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-960">
            Frequently Asked Questions
          </h2>
          <FAQAccordion items={faqs} data-id="element-961" />
        </div>
      </section>

      <CTAStrip heading="Ready to Book Your House Clearance?" subheading="Get a free, no-obligation quote. We reply within 30 minutes." data-id="element-962" />
    </div>;
}